/**
 * Cesium Waypoint Renderer Utility
 *
 * Handles rendering of waypoints and flight paths on Cesium 3D globe
 * Provides click-to-add and drag-to-move functionality
 */

import {
  Viewer,
  Entity,
  Cartesian3,
  Color,
  VerticalOrigin,
  Cartesian2,
  Cartographic,
  Math as CesiumMath,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
  defined,
  HeightReference,
  ArcType,
  ColorMaterialProperty,
  ConstantProperty,
} from 'cesium';

import type { Waypoint } from '../types/waypoint.types';
import type { Position } from '../types/mission.types';
import { VISUAL_DEFAULTS } from '@/constants/mission-defaults';
import { MavCommand, getCommandName } from '@/constants/mavlink-commands';

/**
 * Get color for waypoint based on its state and command
 */
function getWaypointColor(
  waypoint: Waypoint,
  isSelected: boolean,
  isHovered: boolean
): Color {
  if (isSelected) {
    return Color.fromCssColorString(VISUAL_DEFAULTS.waypointColorSelected);
  }
  if (isHovered) {
    return Color.fromCssColorString(VISUAL_DEFAULTS.waypointColorHovered);
  }
  if (waypoint.isCurrentWaypoint) {
    return Color.fromCssColorString(VISUAL_DEFAULTS.waypointColorCurrent);
  }

  // Default color based on command type
  switch (waypoint.command) {
    case MavCommand.NAV_TAKEOFF:
      return Color.LIME;
    case MavCommand.NAV_LAND:
      return Color.ORANGE;
    case MavCommand.NAV_RETURN_TO_LAUNCH:
      return Color.YELLOW;
    default:
      return Color.fromCssColorString(VISUAL_DEFAULTS.waypointColorDefault);
  }
}

/**
 * Get marker size based on selection state
 */
function getMarkerSize(isSelected: boolean, isHovered: boolean): number {
  if (isSelected) {
    return VISUAL_DEFAULTS.waypointMarkerSizeSelected;
  }
  if (isHovered) {
    return VISUAL_DEFAULTS.waypointMarkerSizeHovered;
  }
  return VISUAL_DEFAULTS.waypointMarkerSize;
}

/**
 * Add waypoint marker to Cesium viewer
 *
 * @param viewer - Cesium viewer instance
 * @param waypoint - Waypoint to render
 * @param isSelected - Whether waypoint is selected
 * @param isHovered - Whether waypoint is hovered
 * @returns Created Cesium entity
 */
export function addWaypointMarker(
  viewer: Viewer,
  waypoint: Waypoint,
  isSelected: boolean = false,
  isHovered: boolean = false
): Entity {
  const position = Cartesian3.fromDegrees(
    waypoint.longitude,
    waypoint.latitude,
    waypoint.altitude
  );

  const color = getWaypointColor(waypoint, isSelected, isHovered);
  const size = getMarkerSize(isSelected, isHovered);

  const entity = viewer.entities.add({
    id: waypoint.id,
    position: position,

    // Point marker
    point: {
      pixelSize: size / 4,
      color: color,
      outlineColor: Color.WHITE,
      outlineWidth: 2,
      heightReference: HeightReference.NONE,
    },

    // Label (sequence number and command)
    label: {
      text: `${waypoint.sequence + 1}`,
      font: 'bold 16px sans-serif',
      fillColor: Color.WHITE,
      outlineColor: Color.BLACK,
      outlineWidth: 3,
      verticalOrigin: VerticalOrigin.BOTTOM,
      pixelOffset: new Cartesian2(0, -20),
      showBackground: true,
      backgroundColor: Color.fromAlpha(Color.BLACK, 0.7),
      backgroundPadding: new Cartesian2(8, 4),
      disableDepthTestDistance: Number.POSITIVE_INFINITY,
    },

    // Description for tooltip
    description: `
      <strong>Waypoint ${waypoint.sequence + 1}</strong><br/>
      Command: ${getCommandName(waypoint.command)}<br/>
      Lat: ${waypoint.latitude.toFixed(6)}°<br/>
      Lng: ${waypoint.longitude.toFixed(6)}°<br/>
      Alt: ${waypoint.altitude.toFixed(1)}m
    `,
  });

  return entity;
}

/**
 * Update waypoint marker appearance
 *
 * @param viewer - Cesium viewer instance
 * @param waypoint - Waypoint data
 * @param isSelected - Whether waypoint is selected
 * @param isHovered - Whether waypoint is hovered
 */
export function updateWaypointMarker(
  viewer: Viewer,
  waypoint: Waypoint,
  isSelected: boolean,
  isHovered: boolean
): void {
  const entity = viewer.entities.getById(waypoint.id);
  if (!entity) return;

  // Update position
  // @ts-expect-error - Cesium type system requires Property but accepts Cartesian3
  entity.position = Cartesian3.fromDegrees(
    waypoint.longitude,
    waypoint.latitude,
    waypoint.altitude
  );

  // Update appearance
  const color = getWaypointColor(waypoint, isSelected, isHovered);
  const size = getMarkerSize(isSelected, isHovered);

  if (entity.point) {
    entity.point.pixelSize = new ConstantProperty(size / 4);
    entity.point.color = new ConstantProperty(color);
  }

  if (entity.label) {
    entity.label.text = new ConstantProperty(`${waypoint.sequence + 1}`);
  }
}

/**
 * Remove waypoint marker from viewer
 *
 * @param viewer - Cesium viewer instance
 * @param waypointId - ID of waypoint to remove
 */
export function removeWaypointMarker(viewer: Viewer, waypointId: string): void {
  viewer.entities.removeById(waypointId);
}

/**
 * Remove all waypoint markers
 *
 * @param viewer - Cesium viewer instance
 */
export function removeAllWaypointMarkers(viewer: Viewer): void {
  viewer.entities.removeAll();
}

/**
 * Render flight path polyline between waypoints
 *
 * @param viewer - Cesium viewer instance
 * @param waypoints - Array of waypoints
 * @param pathId - Optional ID for the path entity (default: 'flight-path')
 * @returns Created polyline entity
 */
export function renderFlightPath(
  viewer: Viewer,
  waypoints: Waypoint[],
  pathId: string = 'flight-path'
): Entity | null {
  // Remove existing path if any
  viewer.entities.removeById(pathId);

  if (waypoints.length < 2) {
    return null;
  }

  const positions: Cartesian3[] = waypoints.map((wp) =>
    Cartesian3.fromDegrees(wp.longitude, wp.latitude, wp.altitude)
  );

  const pathEntity = viewer.entities.add({
    id: pathId,
    polyline: {
      positions: positions,
      width: VISUAL_DEFAULTS.flightPathWidth,
      material: new ColorMaterialProperty(
        Color.fromCssColorString(VISUAL_DEFAULTS.flightPathColor).withAlpha(0.8)
      ),
      clampToGround: false,
      arcType: ArcType.GEODESIC,
    },
  });

  return pathEntity;
}

/**
 * Render altitude profile visualization (vertical lines showing altitude changes)
 *
 * @param viewer - Cesium viewer instance
 * @param waypoints - Array of waypoints
 */
export function renderAltitudeProfile(
  viewer: Viewer,
  waypoints: Waypoint[]
): void {
  // Remove existing altitude indicators
  const toRemove = viewer.entities.values.filter((entity) =>
    entity.id?.toString().startsWith('altitude-indicator-')
  );
  toRemove.forEach((entity) => viewer.entities.remove(entity));

  // Draw vertical lines for significant altitude changes
  for (let i = 1; i < waypoints.length; i++) {
    const wp1 = waypoints[i - 1];
    const wp2 = waypoints[i];

    const altitudeChange = Math.abs(wp2.altitude - wp1.altitude);

    // Only show if altitude change is significant (> 5m)
    if (altitudeChange > 5) {
      viewer.entities.add({
        id: `altitude-indicator-${i}`,
        polyline: {
          positions: [
            Cartesian3.fromDegrees(wp2.longitude, wp2.latitude, wp1.altitude),
            Cartesian3.fromDegrees(wp2.longitude, wp2.latitude, wp2.altitude),
          ],
          width: 2,
          material: new ColorMaterialProperty(Color.YELLOW.withAlpha(0.5)),
          clampToGround: false,
        },
      });
    }
  }
}

/**
 * Enable click-to-add waypoint mode
 *
 * @param viewer - Cesium viewer instance
 * @param onWaypointAdd - Callback when waypoint is added
 * @returns Cleanup function to disable the mode
 */
export function enableAddWaypointMode(
  viewer: Viewer,
  onWaypointAdd: (position: Position) => void
): () => void {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

  handler.setInputAction((click: { position: Cartesian2 }) => {
    // Pick position on globe
    const ray = viewer.camera.getPickRay(click.position);
    if (!ray) return;

    const cartesian = viewer.scene.globe.pick(ray, viewer.scene);

    if (defined(cartesian)) {
      const cartographic = Cartographic.fromCartesian(cartesian);
      const latitude = CesiumMath.toDegrees(cartographic.latitude);
      const longitude = CesiumMath.toDegrees(cartographic.longitude);
      const altitude = cartographic.height;

      onWaypointAdd({ latitude, longitude, altitude });
    }
  }, ScreenSpaceEventType.LEFT_CLICK);

  // Return cleanup function
  return () => {
    handler.destroy();
  };
}

/**
 * Enable waypoint dragging
 *
 * @param viewer - Cesium viewer instance
 * @param onWaypointMove - Callback when waypoint is moved
 * @returns Cleanup function to disable dragging
 */
export function enableWaypointDragging(
  viewer: Viewer,
  onWaypointMove: (waypointId: string, position: Position) => void
): () => void {
  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  let draggedEntity: Entity | undefined;

  // Mouse down - start drag
  handler.setInputAction((click: { position: Cartesian2 }) => {
    const pickedObject = viewer.scene.pick(click.position);
    if (defined(pickedObject) && defined(pickedObject.id)) {
      const entity = pickedObject.id as Entity;
      // Only allow dragging waypoint entities (not the flight path)
      if (entity.id && entity.id !== 'flight-path') {
        draggedEntity = entity;
        viewer.scene.screenSpaceCameraController.enableRotate = false;
      }
    }
  }, ScreenSpaceEventType.LEFT_DOWN);

  // Mouse move - drag
  handler.setInputAction((movement: { endPosition: Cartesian2 }) => {
    if (draggedEntity) {
      const ray = viewer.camera.getPickRay(movement.endPosition);
      if (!ray) return;

      const cartesian = viewer.scene.globe.pick(ray, viewer.scene);

      if (defined(cartesian)) {
        const cartographic = Cartographic.fromCartesian(cartesian);
        const position: Position = {
          latitude: CesiumMath.toDegrees(cartographic.latitude),
          longitude: CesiumMath.toDegrees(cartographic.longitude),
          altitude: cartographic.height,
        };

        // Update entity position immediately for smooth dragging
        // @ts-expect-error - Cesium type system requires Property but accepts Cartesian3
        draggedEntity.position = cartesian;

        // Notify parent component
        if (draggedEntity.id) {
          onWaypointMove(draggedEntity.id.toString(), position);
        }
      }
    }
  }, ScreenSpaceEventType.MOUSE_MOVE);

  // Mouse up - end drag
  handler.setInputAction(() => {
    if (draggedEntity) {
      draggedEntity = undefined;
      viewer.scene.screenSpaceCameraController.enableRotate = true;
    }
  }, ScreenSpaceEventType.LEFT_UP);

  // Return cleanup function
  return () => {
    handler.destroy();
  };
}

/**
 * Fly camera to waypoint
 *
 * @param viewer - Cesium viewer instance
 * @param waypoint - Waypoint to fly to
 * @param duration - Flight duration in seconds (default: 2)
 */
export function flyToWaypoint(
  viewer: Viewer,
  waypoint: Waypoint,
  duration: number = 2
): void {
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegrees(
      waypoint.longitude,
      waypoint.latitude,
      waypoint.altitude + 500 // Fly to 500m above waypoint
    ),
    duration,
  });
}

/**
 * Fly camera to view all waypoints (fit bounds)
 *
 * @param viewer - Cesium viewer instance
 * @param waypoints - Array of waypoints
 * @param duration - Flight duration in seconds (default: 2)
 */
export function flyToMission(
  viewer: Viewer,
  waypoints: Waypoint[],
  duration: number = 2
): void {
  if (waypoints.length === 0) return;

  // Fly to view all waypoints
  viewer.camera.flyTo({
    destination: Cartesian3.fromDegreesArrayHeights(
      waypoints.flatMap((wp) => [wp.longitude, wp.latitude, wp.altitude])
    )[0],
    duration,
  });

  // Zoom out to see all waypoints
  setTimeout(() => {
    viewer.zoomTo(viewer.entities);
  }, duration * 1000);
}

/**
 * Highlight waypoint (temporary visual effect)
 *
 * @param viewer - Cesium viewer instance
 * @param waypointId - ID of waypoint to highlight
 * @param duration - Highlight duration in milliseconds (default: 1000)
 */
export function highlightWaypoint(
  viewer: Viewer,
  waypointId: string,
  duration: number = 1000
): void {
  const entity = viewer.entities.getById(waypointId);
  if (!entity || !entity.point) return;

  // Store original values
  const originalSize = entity.point.pixelSize;
  const originalColor = entity.point.color;

  // Enlarge and change color
  entity.point.pixelSize = new ConstantProperty(VISUAL_DEFAULTS.waypointMarkerSizeSelected / 2);
  entity.point.color = new ConstantProperty(Color.YELLOW);

  // Restore after duration
  setTimeout(() => {
    if (entity.point) {
      entity.point.pixelSize = originalSize;
      entity.point.color = originalColor;
    }
  }, duration);
}
